import React, { useState, useEffect, useCallback } from "react";
import { Card, Col, Row, Space, Spin, Table, Tag, Descriptions } from "antd";
import { ValidationService } from "../../../services/ValidationService";
import { usePermissions } from "../../../PermissionsProvider";
import { useParams, Link } from "react-router-dom";
import { ValidationRun } from "../../../model_gen/finetuning";

const ValidationRunView: React.FC = () => {
  const { apiKey } = usePermissions();
  const { validationRunId } = useParams();
  const [validationRun, setValidationRun] = useState<ValidationRun>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return "success";
      case "FAILED":
        return "error";
      default:
        return "default";
    }
  };

  const fetchData = useCallback(
    (isRefreshing: boolean) => {
      if (!validationRunId) return;
      const loadingHandler = isRefreshing ? null : setIsLoading;
      ValidationService.getValidationRequests(
        validationRunId,
        loadingHandler,
        apiKey
      ).subscribe(
        (data: ValidationRun) => {
          setValidationRun(data);
        },
        (error: any) => {
          console.error("Error fetching validation requests:", error);
        }
      );
    },
    [apiKey, validationRunId]
  );

  useEffect(() => {
    fetchData(false);

    // Set up auto-refresh interval
    const intervalId = setInterval(() => {
      fetchData(true);
    }, 15000);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchData]);

  return (
    <Card className="validation-wrapper">
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size="middle">
              <h1>Validation Results</h1>
            </Space>
          </Col>
        </Row>
        {!isLoading && validationRun && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Company">
                  {validationRun.company_name}
                </Descriptions.Item>
                {validationRun.application_name && (
                  <Descriptions.Item label="Application">
                    {validationRun.application_name}
                  </Descriptions.Item>
                )}
                {validationRun.intent_name && (
                  <Descriptions.Item label="Intent">
                    {validationRun.intent_name}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Finetune Run">
                  {validationRun.finetune_run_id && (
                    <Link
                      to={`/models/finetuning/${validationRun.finetune_run_id}`}>
                      View Finetune Run
                    </Link>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Run Date">
                  {new Date(validationRun.date_created).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Model">
                  {validationRun.model}
                </Descriptions.Item>
                <Descriptions.Item label="Description">
                  {validationRun.description || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={getStatusColor(validationRun.status)}>
                    {validationRun.status?.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Accuracy">
                  {validationRun.accuracy !== undefined
                    ? `${(validationRun.accuracy * 100).toFixed(2)}% (${
                        validationRun.correct_requests
                      } / ${validationRun.total_requests})`
                    : validationRun.requests?.length > 0
                    ? `${(
                        (validationRun.requests.filter((req) => req.correct)
                          .length /
                          validationRun.requests.length) *
                        100
                      ).toFixed(2)}% (${
                        validationRun.requests.filter((req) => req.correct)
                          .length
                      } / ${validationRun.requests.length})`
                    : "N/A"}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        )}
        {isLoading ? (
          <Spin
            size="large"
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "20px",
            }}
          />
        ) : (
          <div style={{ overflowX: "auto", width: "100%" }}>
            <Table
              dataSource={validationRun.requests}
              rowKey="request_id"
              scroll={{ x: 1500 }}
              columns={[
                {
                  title: "Request ID",
                  dataIndex: "request_id",
                  key: "request_id",
                  width: 200,
                  fixed: "left",
                },
                {
                  title: "Status",
                  dataIndex: "correct",
                  key: "correct",
                  width: 100,
                  fixed: "left",
                  sorter: (a, b) => {
                    if (a.correct === b.correct) return 0;
                    return a.correct ? 1 : -1;
                  },
                  render: (correct) => (
                    <Tag color={correct ? "success" : "error"}>
                      {correct ? "Correct" : "Incorrect"}
                    </Tag>
                  ),
                },
                {
                  title: "Expected Output",
                  dataIndex: "expected_output",
                  key: "expected_output",
                  width: 300,
                  render: (text) => (
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        maxHeight: "150px",
                        overflow: "auto",
                      }}>
                      {text}
                    </div>
                  ),
                },
                {
                  title: "Actual Output",
                  dataIndex: "actual_output",
                  key: "actual_output",
                  width: 300,
                  render: (text) => (
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        maxHeight: "150px",
                        overflow: "auto",
                      }}>
                      {text}
                    </div>
                  ),
                },
                {
                  title: "Diff",
                  dataIndex: "diff",
                  key: "diff",
                  width: 300,
                  render: (text) => (
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        maxHeight: "150px",
                        overflow: "auto",
                        fontFamily: "monospace",
                      }}>
                      {text}
                    </div>
                  ),
                },
                {
                  title: "Date",
                  dataIndex: "date_created",
                  key: "date_created",
                  width: "150px",
                  sorter: (a, b) => a.date_created - b.date_created,
                  render: (date) => new Date(date).toLocaleString(),
                },
              ]}
            />
          </div>
        )}
      </Space>
    </Card>
  );
};

export default ValidationRunView;
